// ** React Imports
import { useState, useEffect, ChangeEvent } from 'react'

// ** Type Imports
import { GoogleTagManagerType, IntegrationsType } from 'src/lib/types/integrations'

// ** Child Imports
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { Switch, Grid, Typography, TextField } from '@mui/material'
import SaveNotification from 'src/components/common/SaveNotification'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig } from 'src/lib/core/api-methods'
import { initialGoogleTagManagerState } from 'src/lib/utils/integrationsHelpers'
import { alertSeverites } from 'src/lib/utils/constants'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { useLocale } from 'src/lib/hooks/useLocal'
import { getEnabledChannelsArray } from 'src/lib/utils/parsers'

interface GoogleTagManagerProps {
  isOnboarding?: boolean
  handleOnboardingSave?: () => void
}

const GoogleTagManager: React.FC<GoogleTagManagerProps> = ({ isOnboarding = false, handleOnboardingSave }) => {
  // Context hook
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { gtm } = integrations || {}
  const { t } = useLocale()

  //Local State
  const [googleTagManager, setGoogleTagManager] = useState<GoogleTagManagerType>(initialGoogleTagManagerState)
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary) return
    setGoogleTagManager(temporary.integrations?.gtm)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(integrations?.gtm) !== JSON.stringify(googleTagManager)))
  }, [integrations?.gtm, googleTagManager])

  useEffect(() => {
    if (!gtm) return
    setGoogleTagManager(gtm)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [gtm, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            gtm: googleTagManager
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response: any = await getIntegrations()
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations(response?.data)
      }
    } catch (error) {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const getPayload = () => {
    return {
      enabled: googleTagManager?.enabled,
      container_id: googleTagManager?.containerId?.trim(),
      channels: getEnabledChannelsArray(googleTagManager?.channels)
    }
  }

  const callSubmit = async () => {
    if (!validateFields()) return
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const response: any = await setAnalyticsConfig('gtm', getPayload())
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations({ ...integrations, gtm: googleTagManager }, false)
        setOpenAlert('success')
        setAlertMessage('Successfully saved analytics configuration!')
        handleOnboardingSave && handleOnboardingSave()
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errorMessage
        ? error?.response?.data?.errorMessage
        : `Could not save configuration!`
      handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
      console.error(error)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const validateFields = () => {
    if (googleTagManager?.enabled && !Boolean(googleTagManager?.containerId?.length)) {
      setOpenAlert('error')
      setAlertMessage('Please enter a valid container ID!')
      return false
    }
    return true
  }

  const handleDiscardSaving = () => {
    if (!gtm) return
    setGoogleTagManager(gtm)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <IntegrationChannels
        channels={googleTagManager.channels ?? []}
        onChange={(channels: any) => {
          setGoogleTagManager({ ...googleTagManager, channels: channels })
        }}
        hideChannels={!googleTagManager?.enabled}
      >
        <Grid container sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Grid container>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>Google Tag Manager is</Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${googleTagManager?.enabled ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <Switch
                name='GoogleTagManagerToggle'
                checked={googleTagManager?.enabled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setGoogleTagManager({ ...googleTagManager, enabled: event?.target?.checked })
                }
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid xs={6} md={4} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>Container ID</Typography>
            </Grid>
            <Grid item xs={6} md={8} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <TextField
                fullWidth
                type='text'
                size='small'
                variant='outlined'
                value={googleTagManager?.containerId}
                placeholder='Container ID'
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setGoogleTagManager({ ...googleTagManager, containerId: event?.target?.value })
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </IntegrationChannels>
      {!Boolean(isOnboarding) ? (
        <SaveNotification onSave={callSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />
      ) : (
        <>
          <button
            className='px-3 py-2 ml-auto text-sm font-bold border rounded-lg w-fit text-[#FFFFFF] bg-[#01CCA7]'
            onClick={callSubmit}
          >
            {t('save')}
          </button>
        </>
      )}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}

export default GoogleTagManager
